/**
 * Conversion goal pages (§12) — dedicated same-tab success views so Google Ads
 * can register them as goal pages. Two variants:
 *   - "welcome"          → /welcome          (first successful sign-in)
 *   - "booking-confirmed" → /booking-confirmed (first meeting booked)
 * First-time-only: the route is only rendered the first time (the frontend
 * marks the corresponding celebration flag via POST /api/v1/me/celebrations,
 * persisted on the profile so it's once-per-user, not once-per-device).
 * British English.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { useSession } from '../context/SessionContext';
import { api } from '../lib/api';
import './GoalPage.css';

type Variant = 'welcome' | 'booking-confirmed';

interface GoalPageProps {
  variant: Variant;
}

const CONTENT: Record<Variant, { overline: string; title: string; desc: string; cta: string; to: string }> = {
  welcome: {
    overline: 'Welcome',
    title: "You're in",
    desc: 'Your Idea Toolkit account is ready. Let’s start exploring your idea — safely and confidentially.',
    cta: 'Go to your dashboard',
    to: '/',
  },
  'booking-confirmed': {
    overline: 'Confirmed',
    title: 'Your review is booked',
    desc: 'Your confidential idea review is booked with your matched Innovation Coach. A calendar reminder is on its way.',
    cta: 'Go to the Alert Centre',
    to: '/alerts',
  },
};

export function GoalPage({ variant }: GoalPageProps) {
  const navigate = useNavigate();
  const session = useSession();
  const [redirecting, setRedirecting] = useState(false);
  const content = CONTENT[variant];

  // Mark the celebration flag (first-time). Best-effort — the page still
  // renders even if the BFF call fails (the user shouldn't be blocked).
  useEffect(() => {
    const flag = variant === 'welcome' ? 'firstSignIn' : 'booking';
    api
      .post('/api/v1/me/celebrations', { [flag]: true })
      .catch(() => {
        // Non-fatal — the goal page still registers in GA4/GTM via the route hit.
      });
    session.markCelebration(flag as 'firstSignIn');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = () => {
    setRedirecting(true);
    navigate(content.to);
  };

  return (
    <div className="itk-goal">
      <div className="itk-goal-card">
        <div className="itk-goal-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div className="itk-goal-overline">{content.overline}</div>
        <h1 className="itk-goal-title">{content.title}</h1>
        <p className="itk-goal-desc">{content.desc}</p>
        <div className="itk-goal-actions">
          <Button loading={redirecting} onClick={go} style={{ width: '100%' }}>
            {content.cta}
          </Button>
        </div>
      </div>
    </div>
  );
}